import type { AlcanceDeConfiguracoes } from "./api"
import * as Icones from "./componentes/Icones"

/**
 * OS ITENS DE CONFIGURAÇÕES (spec 0104), fora da tela, para o roteador, a
 * trilha e o menu principal usarem sem importar `Configuracoes.tsx`.
 *
 * A ordem e o grupo de cada item vêm do backend (`ITENS` em
 * `backend/app/services/alcance.py`); aqui ficam só as chaves que o endereço
 * aceita e o que a tela escreve de cada item.
 */
export const ITENS_DE_CONFIGURACAO = [
  "entidades",
  "setores",
  "contas",
  "papeis",
  "tipos-de-entidade",
  "tipos-de-setor",
  "cargos",
  "sistemas",
  "parametros",
  "faixas",
  "tipos",
  "athus",
  "leitura",
  "canais",
  "auditoria",
  "senha",
] as const

export type ItemDeConfiguracao = (typeof ITENS_DE_CONFIGURACAO)[number]

export type FiltroDoEndereco = "perfil" | "cargo" | "tipo" | "sistema"

export const FILTROS_DO_ITEM: Partial<Record<ItemDeConfiguracao, FiltroDoEndereco[]>> = {
  contas: ["perfil", "cargo"],
  entidades: ["tipo", "sistema"],
  setores: ["tipo"],
}

export function ehItemDeConfiguracao(valor: string): valor is ItemDeConfiguracao {
  return (ITENS_DE_CONFIGURACAO as readonly string[]).includes(valor)
}

export type DadosDoItem = { rotulo: string; dica: string; icone: Icones.Icone }

export const DADOS_DO_ITEM: Record<ItemDeConfiguracao, DadosDoItem> = {
  entidades: { rotulo: "Entidades", dica: "O cadastro das entidades atendidas pelo sistema", icone: Icones.Orgao },
  setores: { rotulo: "Setores", dica: "A estrutura de setores da entidade, onde as contas são lotadas", icone: Icones.Organograma },
  contas: { rotulo: "Contas e permissões", dica: "Quem entra no sistema, com que papel, e a senha de cada conta", icone: Icones.Pessoas },
  papeis: { rotulo: "Perfis de acesso", dica: "O que gestor, fiscal e leitura podem fazer no sistema", icone: Icones.Permissao },
  "tipos-de-entidade": { rotulo: "Tipos de entidade", dica: "A classificação das entidades", icone: Icones.Etiqueta },
  "tipos-de-setor": { rotulo: "Tipos de setor", dica: "A classificação dos setores das entidades", icone: Icones.Etiqueta },
  cargos: { rotulo: "Cargos", dica: "O catálogo de cargos das contas", icone: Icones.Cargo },
  sistemas: { rotulo: "Sistemas DATAFull", dica: "O catálogo dos sistemas DATAFull que uma entidade pode usar", icone: Icones.Catalogo },
  parametros: {
    rotulo: "Limites legais e prazos",
    dica: "Os limites e prazos que o sistema usa nos cálculos, com a data de cada valor",
    icone: Icones.LimiteLegal,
  },
  faixas: {
    rotulo: "Faixas de providência",
    dica: "Quanto tempo antes do término cada providência de continuidade deve estar feita",
    icone: Icones.Providencia,
  },
  tipos: {
    rotulo: "Tipos de peça do processo",
    dica: "Os tipos de documento do processo e o papel de cada um na fiscalização",
    icone: Icones.TipoDePeca,
  },
  athus: { rotulo: "ATHUS", dica: "A ligação com o ATHUS, de onde vêm os processos e os documentos", icone: Icones.Integracao },
  leitura: {
    rotulo: "Leitura dos contratos",
    dica: "O que o sistema leu no documento de cada contrato, campo a campo",
    icone: Icones.Leitura,
  },
  canais: { rotulo: "Canais de aviso", dica: "E-mail e WhatsApp: se estão prontos para enviar avisos", icone: Icones.Canal },
  auditoria: { rotulo: "Auditoria", dica: "Quem fez o quê e quando, em cada ação que alterou dados", icone: Icones.Historico },
  senha: { rotulo: "Política de senha", dica: "As exigências para a senha das contas", icone: Icones.Chave },
}

export const ROTULO_DA_AREA: Record<string, string> = {
  organizacao: "Organização",
  catalogos: "Catálogos gerais",
  regras: "Regras do sistema",
  integracoes: "Integrações",
  seguranca: "Segurança",
}

export const ICONE_DA_AREA: Record<string, Icones.Icone> = {
  organizacao: Icones.Organograma,
  catalogos: Icones.Catalogo,
  regras: Icones.Fundamento,
  integracoes: Icones.Integracao,
  seguranca: Icones.Permissao,
}

export type GrupoDeConfiguracao = { area: string; rotulo: string; itens: ItemDeConfiguracao[] }

/** Os grupos com os itens que a conta vê, na ordem do backend; grupo vazio não entra. */
export function gruposDeConfiguracao(alcance: AlcanceDeConfiguracoes): GrupoDeConfiguracao[] {
  const grupos: GrupoDeConfiguracao[] = []
  for (const { chave, area, ve } of alcance.itens) {
    const rotulo = ROTULO_DA_AREA[area]
    if (!ve || rotulo === undefined || !ehItemDeConfiguracao(chave)) continue
    let grupo = grupos.find((g) => g.area === area)
    if (grupo === undefined) {
      grupo = { area, rotulo, itens: [] }
      grupos.push(grupo)
    }
    grupo.itens.push(chave)
  }
  return grupos
}

export function primeiroItem(grupos: GrupoDeConfiguracao[]): ItemDeConfiguracao | undefined {
  return grupos[0]?.itens[0]
}

export function grupoDoItem(grupos: GrupoDeConfiguracao[], item: ItemDeConfiguracao): GrupoDeConfiguracao | undefined {
  return grupos.find((g) => g.itens.includes(item))
}

/** O item, se a conta o vê; senão, o primeiro que ela vê. */
export function itemVisivel(
  grupos: GrupoDeConfiguracao[],
  item: ItemDeConfiguracao | undefined,
): ItemDeConfiguracao | undefined {
  if (item !== undefined && grupoDoItem(grupos, item) !== undefined) return item
  return primeiroItem(grupos)
}
